import React, { useEffect, useMemo, useRef } from 'react';
import { TextLink, Typography } from '@guildeducationinc/recess';
import SubdirectoryArrowRightIcon from '@guildeducationinc/recess/icons/SubdirectoryArrowRightIcon';
import { trackEvent } from 'src/services/analytics';
import { GetMemberProgramQuery } from 'src/graphqlTypes/catalogServiceTypes';
import { Skeleton } from 'src/components/UserFacetSkeletonLoader';
import styles from './ProgramDetailsNavigation.module.scss';

export interface Props {
	program: GetMemberProgramQuery['memberProgram'];
	availableSections: NodeListOf<Element>;
	loading: boolean;
}

export const ProgramDetailsNavigation: React.FC<Props> = ({
	program,
	availableSections,
	loading,
}) => {
	const hashesVisited = useRef(new Set<string>());
	const activeLink = useRef(window.location.hash);
	const windowOffset = 100;

	const links = useMemo(
		() =>
			availableSections
				? Array.from(availableSections).map(el => ({
						title: el.getAttribute('data-title'),
						href: `#${el.getAttribute('id')}`,
					}))
				: [],
		[availableSections]
	);

	useEffect(() => {
		if (!links.length) {
			return;
		}
		const { hash } = window.location;
		const activeLink = hash || links[0].href;

		// If we had a section hash in the URL, jump to there
		if (hash) {
			const element = document.querySelector<HTMLElement>(activeLink);
			if (element) {
				window.scrollTo(0, element.offsetTop);
			}
		}
	}, [links]);

	useEffect(() => {
		if (!links.length) {
			return;
		}

		const scrollListener = () => {
			const fromTop = window.scrollY + windowOffset;
			links.forEach(link => {
				const section = document.querySelector<HTMLElement>(link.href);
				if (section) {
					const rect = section.getBoundingClientRect();
					const sectionInActiveView = rect.top < 1 && rect.top > -rect.height;

					if (sectionInActiveView) {
						// Only update the activeLink and call history.replace if a new section has come into view
						if (activeLink.current !== link.href) {
							activeLink.current = link.href;
							if (!hashesVisited.current.has(link.href)) {
								trackEvent('ProgramDetailsImpression', {
									offeringID: program.id,
									product: 'catalog',
									text: link.href.split('#')[1],
									navigationMethod: 'scroll',
								});
							}
							hashesVisited.current.add(link.href);
							window.history.replaceState(null, link.title, link.href);
						}
					}
					if (
						link.href === '#detail-overview' &&
						section.offsetTop > fromTop &&
						activeLink.current !== ''
					) {
						activeLink.current = '';
						window.history.replaceState(null, null, window.location.pathname);
					}
				}
			});
		};

		window.addEventListener('scroll', scrollListener);
		return () => window.removeEventListener('scroll', scrollListener);
	}, [links, activeLink, program?.id]);

	const handleKeyPress = (e, href) => {
		if (e.keyCode === 13) {
			handleClick(href);
		}
	};

	const handleClick = (href: string) => {
		const element = document.querySelector<HTMLElement>(href);
		if (!element) {
			return;
		}
		if (!hashesVisited.current.has(href)) {
			trackEvent('ProgramDetailsImpression', {
				offeringID: program.id,
				product: 'catalog',
				text: href.split('#')[1],
				navigationMethod: 'click',
			});
			hashesVisited.current.add(href);
		}

		trackEvent('Clickable', {
			offeringID: program.id,
			tag: 'program_details_navigation',
			text: href.split('#')[1],
			type: 'link',
		});

		element.scrollIntoView({
			behavior: 'smooth',
		});
	};

	// hook to focus the element that is currently active
	// which allows the user to be notified via screen reader
	useEffect(() => {
		if (!activeLink.current) return;
		// set timeout is used to ensure this always happens after react is finished rendering
		window.setTimeout(() => {
			const element = document.querySelector<HTMLElement>(activeLink.current);
			if (element) {
				element.setAttribute('aria-live', 'polite');
				element.focus({ preventScroll: true });
			}
		}, 500);
	}, [activeLink]);

	return (
		<nav aria-label='Table of contents' className={styles.tableOfContents}>
			<Typography Component='h4' variant='label1'>
				What&apos;s on this page
			</Typography>
			<Skeleton count={2} dataLoading={loading}>
				<ul className={styles.navItemContainer}>
					{links &&
						links.map(l => (
							<li className={styles.navItems} key={`nav-${l.href}`}>
								<TextLink
									className={styles.anchorText}
									data-testid={`nav-${l.href}`}
									href={l.href}
									icon={<SubdirectoryArrowRightIcon size={24} />}
									iconDirection='left'
									key={`nav-${l.href}`}
									onClick={() => handleClick(l.href)}
									onKeyDown={e => handleKeyPress(e, l.href)}
									size='medium'
									tabIndex={0}
									target='_self'
									variant='standalone'
								>
									{l.title}
								</TextLink>
							</li>
						))}
				</ul>
			</Skeleton>
		</nav>
	);
};
